import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from './ui/button';

const API_BASE = "https://kiss-node-orchestrator-1057209991970.us-central1.run.app";

const CADENCE_LABELS = {
  continuous: 'Every 15 min',
  hourly:     'Hourly',
  daily:      'Daily',
  on_demand:  'On demand',
};

const SEVERITY_COLORS = {
  high:   'text-red-400 border-red-600',
  medium: 'text-yellow-400 border-yellow-600',
  low:    'text-green-400 border-green-700',
};

const SOURCE_LABELS = {
  gdelt_doc:     'GDELT',
  rss:           'RSS Feeds',
  hackernews:    'Hacker News',
  arxiv:         'arXiv',
  edgar:         'SEC EDGAR',
  github:        'GitHub',
  fourchan:      '4chan',
  wikileaks:     'WikiLeaks',
  documentcloud: 'DocumentCloud',
  telegram:      'Telegram',
  opensky:       'OpenSky',
  reddit:        'Reddit',
  bluesky:       'Bluesky',
  mastodon:      'Mastodon',
  ddosecrets:    'DDoSecrets',
};

function timeAgo(dateStr) {
  if (!dateStr) return 'never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ─── Create Monitor Form ────────────────────────────────────────────────────

function CreateMonitorForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({
    name:           '',
    keywords:       '',
    persons:        '',
    orgs:           '',
    semantic_query: '',
    cadence:        'hourly',
    sources:        ['gdelt_doc'],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const toggleSource = (src) => {
    setForm(f => ({
      ...f,
      sources: f.sources.includes(src)
        ? f.sources.filter(s => s !== src)
        : [...f.sources, src],
    }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    if (!form.sources.length) { setError('Select at least one source'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/createMonitor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name:     form.name.trim(),
          keywords: form.keywords.split(',').map(s => s.trim()).filter(Boolean),
          entities: {
            persons:   form.persons.split(',').map(s => s.trim()).filter(Boolean),
            orgs:      form.orgs.split(',').map(s => s.trim()).filter(Boolean),
            locations: [],
          },
          semantic_query: form.semantic_query.trim() || null,
          cadence: form.cadence,
          sources: form.sources,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      onCreated(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-600 rounded p-4 bg-gray-900 space-y-3">
      <h3 className="text-green-400 font-semibold">New Monitor</h3>

      <div>
        <label className="text-xs text-gray-400 block mb-1">Name *</label>
        <input
          className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-green-400 text-sm placeholder-gray-500"
          placeholder="e.g. Ukraine conflict"
          value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
        />
      </div>

      <div>
        <label className="text-xs text-gray-400 block mb-1">Keywords (comma-separated)</label>
        <input
          className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-green-400 text-sm placeholder-gray-500"
          placeholder="e.g. sanctions, ceasefire, NATO"
          value={form.keywords}
          onChange={e => setForm(f => ({ ...f, keywords: e.target.value }))}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-400 block mb-1">Persons (comma-separated)</label>
          <input
            className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-green-400 text-sm placeholder-gray-500"
            placeholder="e.g. Zelensky, Macron"
            value={form.persons}
            onChange={e => setForm(f => ({ ...f, persons: e.target.value }))}
          />
        </div>
        <div>
          <label className="text-xs text-gray-400 block mb-1">Organizations (comma-separated)</label>
          <input
            className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-green-400 text-sm placeholder-gray-500"
            placeholder="e.g. NATO, Kremlin"
            value={form.orgs}
            onChange={e => setForm(f => ({ ...f, orgs: e.target.value }))}
          />
        </div>
      </div>

      <div>
        <label className="text-xs text-gray-400 block mb-1">
          Semantic query <span className="text-gray-600">(optional — describe what you're looking for in plain language)</span>
        </label>
        <textarea
          className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-green-400 text-sm placeholder-gray-500 resize-none"
          rows={2}
          placeholder="e.g. military operations near civilian infrastructure in eastern Europe"
          value={form.semantic_query}
          onChange={e => setForm(f => ({ ...f, semantic_query: e.target.value }))}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-400 block mb-1">Cadence</label>
          <select
            className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-green-400 text-sm"
            value={form.cadence}
            onChange={e => setForm(f => ({ ...f, cadence: e.target.value }))}
          >
            {Object.entries(CADENCE_LABELS).map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
        </div>
        <div className="col-span-2">
          <label className="text-xs text-gray-400 block mb-1">Sources</label>
          <div className="flex flex-wrap gap-x-4 gap-y-2 mt-1">
            {Object.entries(SOURCE_LABELS).map(([src, label]) => (
              <label key={src} className="flex items-center gap-1 text-sm text-green-400 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-green-400"
                  checked={form.sources.includes(src)}
                  onChange={() => toggleSource(src)}
                />
                {label}
              </label>
            ))}
          </div>
        </div>
      </div>

      {error && <p className="text-red-400 text-xs">{error}</p>}

      <div className="flex gap-2">
        <Button onClick={handleSubmit} disabled={saving}>
          {saving ? 'Creating…' : 'Create Monitor'}
        </Button>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

// ─── Monitors Tab ───────────────────────────────────────────────────────────

function MonitorsTab() {
  const [monitors, setMonitors]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [runningId, setRunningId]   = useState(null);
  const [runResult, setRunResult]   = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/getMonitors`);
      const data = await res.json();
      setMonitors(data.monitors || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (monitor) => {
    try {
      await fetch(`${API_BASE}/updateMonitor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: monitor.id, is_active: !monitor.is_active }),
      });
      load();
    } catch (e) { console.error(e); }
  };

  const deleteMonitor = async (id) => {
    if (!window.confirm('Delete this monitor?')) return;
    try {
      await fetch(`${API_BASE}/deleteMonitor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      load();
    } catch (e) { console.error(e); }
  };

  const runNow = async (id) => {
    setRunningId(id);
    setRunResult(r => ({ ...r, [id]: null }));
    try {
      const res  = await fetch(`${API_BASE}/runMonitor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      setRunResult(r => ({ ...r, [id]: data }));
      load();
    } catch (e) {
      setRunResult(r => ({ ...r, [id]: { error: e.message } }));
    } finally {
      setRunningId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <span className="text-sm text-gray-400">{monitors.length} monitor{monitors.length !== 1 ? 's' : ''}</span>
        <Button onClick={() => setShowCreate(s => !s)}>
          {showCreate ? 'Cancel' : '+ New Monitor'}
        </Button>
      </div>

      {showCreate && (
        <CreateMonitorForm
          onCreated={() => { setShowCreate(false); load(); }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {loading && <p className="text-gray-400 text-sm">Loading…</p>}

      {!loading && monitors.length === 0 && !showCreate && (
        <p className="text-gray-400 text-sm italic">No monitors yet. Create one to start tracking topics.</p>
      )}

      {monitors.map(m => {
        const tags = [
          ...(m.keywords || []),
          ...((m.entities?.persons) || []),
          ...((m.entities?.orgs) || []),
        ];
        const result = runResult[m.id];

        return (
          <div
            key={m.id}
            className={`border rounded p-4 bg-gray-900 ${m.is_active ? 'border-gray-600' : 'border-gray-700 opacity-60'}`}
          >
            <div className="flex justify-between items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-green-400">{m.name}</span>
                  <span className="text-xs text-gray-500 border border-gray-700 rounded px-1">
                    {CADENCE_LABELS[m.cadence]}
                  </span>
                  {(m.sources || []).map(s => (
                    <span key={s} className="text-xs text-blue-400 border border-blue-800 rounded px-1">
                      {SOURCE_LABELS[s] || s}
                    </span>
                  ))}
                  {m.semantic_query && (
                    <span className={`text-xs rounded px-1 border ${m.semantic_vector ? 'text-purple-400 border-purple-800' : 'text-gray-500 border-gray-700'}`}>
                      {m.semantic_vector ? 'semantic ✓' : 'semantic (computing…)'}
                    </span>
                  )}
                  {!m.is_active && (
                    <span className="text-xs text-gray-500 italic">paused</span>
                  )}
                </div>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {tags.map((t, i) => (
                      <span key={i} className="text-xs bg-gray-800 text-gray-300 rounded px-1.5 py-0.5">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">Last run: {timeAgo(m.last_run_at)}</p>
                {result && (
                  <p className={`text-xs mt-1 ${result.error ? 'text-red-400' : 'text-green-400'}`}>
                    {result.error
                      ? `Error: ${result.error}`
                      : `Fetched ${result.fetched}, ${result.new} new${result.alerts?.length ? `, ${result.alerts.length} alert(s)` : ''}`}
                  </p>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="outline"
                  onClick={() => runNow(m.id)}
                  disabled={runningId === m.id}
                >
                  {runningId === m.id ? 'Running…' : 'Run Now'}
                </Button>
                <Button variant="outline" onClick={() => toggleActive(m)}>
                  {m.is_active ? 'Pause' : 'Resume'}
                </Button>
                <Button variant="outline" onClick={() => deleteMonitor(m.id)}>
                  Delete
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Feed Tab ────────────────────────────────────────────────────────────────

function FeedTab() {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [source, setSource]   = useState('');
  const [offset, setOffset]   = useState(0);
  const LIMIT = 25;

  const load = useCallback(async (reset = false) => {
    setLoading(true);
    const currentOffset = reset ? 0 : offset;
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset: currentOffset });
      if (keyword) params.set('keyword', keyword);
      if (source)  params.set('source', source);
      const res  = await fetch(`${API_BASE}/getFeedItems?${params}`);
      const data = await res.json();
      const newItems = data.items || [];
      setItems(reset ? newItems : prev => [...prev, ...newItems]);
      if (reset) setOffset(LIMIT); else setOffset(o => o + LIMIT);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [keyword, source, offset]);

  useEffect(() => { load(true); }, [keyword, source]); // eslint-disable-line

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        <input
          className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-green-400 text-sm placeholder-gray-500 flex-1 min-w-48"
          placeholder="Search keyword…"
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
        />
        <select
          className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-green-400 text-sm"
          value={source}
          onChange={e => setSource(e.target.value)}
        >
          <option value="">All sources</option>
          {Object.entries(SOURCE_LABELS).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
      </div>

      {loading && items.length === 0 && (
        <p className="text-gray-400 text-sm">Loading…</p>
      )}

      {!loading && items.length === 0 && (
        <p className="text-gray-400 text-sm italic">No items yet. Run a monitor to populate the feed.</p>
      )}

      <div className="space-y-2">
        {items.map(item => (
          <div key={item.id} className="border border-gray-700 rounded p-3 bg-gray-900 hover:border-gray-500 transition-colors">
            <div className="flex items-start justify-between gap-2">
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-400 text-sm font-medium hover:text-green-300 leading-snug flex-1"
              >
                {item.title || item.url}
              </a>
              <span className="text-xs text-gray-500 shrink-0">{timeAgo(item.published_at)}</span>
            </div>
            <div className="flex gap-2 mt-1.5 flex-wrap">
              <span className="text-xs text-blue-400 border border-blue-800 rounded px-1">
                {SOURCE_LABELS[item.source] || item.source}
              </span>
              {item.source_country && (
                <span className="text-xs text-gray-500">{item.source_country}</span>
              )}
              {item.language && item.language !== 'English' && item.language !== 'en' && (
                <span className="text-xs text-gray-500">{item.language}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {items.length > 0 && items.length % LIMIT === 0 && (
        <Button variant="outline" onClick={() => load(false)} disabled={loading}>
          {loading ? 'Loading…' : 'Load more'}
        </Button>
      )}
    </div>
  );
}

// ─── Article Content Viewer ──────────────────────────────────────────────────

function ArticleContent({ url, cachedStatus }) {
  const [state, setState] = useState(
    cachedStatus === 'success' ? 'cached' : 'idle'
  );
  const [content, setContent] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetch_ = async () => {
    setState('loading');
    try {
      const res = await fetch(`${API_BASE}/fetchContent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });
      const data = await res.json();
      if (data.content) {
        setContent(data.content);
        setState('loaded');
        setExpanded(true);
      } else {
        setState(data.status || 'unavailable');
      }
    } catch {
      setState('error');
    }
  };

  const btnLabel = {
    idle:        'Read Article',
    cached:      'Read Article',
    loading:     'Loading…',
    loaded:      expanded ? 'Collapse' : 'Expand',
    unavailable: 'Unavailable',
    paywall:     'Paywalled',
    error:       'Error',
  }[state] ?? 'Read Article';

  const canFetch = state === 'idle' || state === 'cached';
  const canToggle = state === 'loaded';

  return (
    <div>
      <button
        onClick={canToggle ? () => setExpanded(e => !e) : canFetch ? fetch_ : undefined}
        disabled={state === 'loading' || state === 'unavailable' || state === 'paywall' || state === 'error'}
        className="text-xs text-blue-400 hover:text-blue-300 disabled:text-gray-600 disabled:cursor-not-allowed"
      >
        {btnLabel}
        {state === 'cached' && <span className="text-gray-600 ml-1">(cached)</span>}
      </button>
      {expanded && content && (
        <div className="mt-2 text-xs text-gray-300 bg-gray-800 rounded p-3 max-h-48 overflow-y-auto leading-relaxed whitespace-pre-wrap">
          {content.slice(0, 3000)}{content.length > 3000 ? '…' : ''}
        </div>
      )}
    </div>
  );
}

// ─── Alerts Tab ──────────────────────────────────────────────────────────────

function AlertsTab() {
  const [alerts, setAlerts]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showAcknowledged, setShowAck]    = useState(false);
  const [acknowledging, setAcknowledging] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (!showAcknowledged) params.set('unacknowledged_only', 'true');
      const res  = await fetch(`${API_BASE}/getMonitorAlerts?${params}`);
      const data = await res.json();
      setAlerts(data.alerts || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [showAcknowledged]);

  useEffect(() => { load(); }, [load]);

  const acknowledge = async (id) => {
    setAcknowledging(id);
    try {
      await fetch(`${API_BASE}/acknowledgeAlert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      load();
    } catch (e) { console.error(e); } finally {
      setAcknowledging(null);
    }
  };

  const unackedCount = alerts.filter(a => !a.acknowledged).length;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <span className="text-sm text-gray-400">
          {unackedCount > 0
            ? <span className="text-yellow-400">{unackedCount} unacknowledged</span>
            : 'No unacknowledged alerts'}
        </span>
        <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
          <input
            type="checkbox"
            className="accent-green-400"
            checked={showAcknowledged}
            onChange={e => setShowAck(e.target.checked)}
          />
          Show acknowledged
        </label>
      </div>

      {loading && <p className="text-gray-400 text-sm">Loading…</p>}

      {!loading && alerts.length === 0 && (
        <p className="text-gray-400 text-sm italic">No alerts. Anomalies will appear here when detected.</p>
      )}

      <div className="space-y-3">
        {alerts.map(alert => (
          <div
            key={alert.id}
            className={`border rounded p-4 bg-gray-900 ${SEVERITY_COLORS[alert.severity] || 'border-gray-600 text-green-400'} ${alert.acknowledged ? 'opacity-50' : ''}`}
          >
            <div className="flex justify-between items-start gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wide">
                    {alert.alert_type?.replace('_', ' ')}
                  </span>
                  {alert.severity && (
                    <span className="text-xs border rounded px-1 capitalize">{alert.severity}</span>
                  )}
                  <span className="text-xs text-gray-500">{alert.monitor_name}</span>
                </div>
                <p className="text-sm">{alert.summary}</p>
                <p className="text-xs text-gray-500 mt-1">{timeAgo(alert.created_at)}</p>
                {alert.items && alert.items.length > 0 && (
                  <div className="mt-2 space-y-2 border-t border-gray-700 pt-2">
                    {alert.items.slice(0, 5).map(item => (
                      <div key={item.id} className="space-y-1">
                        <div className="flex items-start gap-2">
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-green-400 hover:text-green-300 flex-1 leading-snug"
                          >
                            {item.title || item.url}
                          </a>
                        </div>
                        <ArticleContent
                          url={item.url}
                          cachedStatus={item.fetch_status}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {!alert.acknowledged && (
                <Button
                  variant="outline"
                  onClick={() => acknowledge(alert.id)}
                  disabled={acknowledging === alert.id}
                >
                  {acknowledging === alert.id ? '…' : 'Dismiss'}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Insights Tab ────────────────────────────────────────────────────────────

function InsightsTab() {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/getFeedItems?limit=500&offset=0`)
      .then(r => r.json())
      .then(d => setItems(d.items || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Source breakdown — sorted by count desc
  const bySource = useMemo(() => {
    const counts = {};
    for (const item of items) {
      counts[item.source] = (counts[item.source] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [items]);

  // Hourly volume buckets for last 24h
  const hourlyBuckets = useMemo(() => {
    const now = Date.now();
    const buckets = Array.from({ length: 24 }, (_, i) => {
      const t = new Date(now - (23 - i) * 3600000);
      return { label: `${t.getHours()}:00`, count: 0 };
    });
    for (const item of items) {
      if (!item.published_at) continue;
      const hoursAgo = Math.floor((now - new Date(item.published_at).getTime()) / 3600000);
      if (hoursAgo >= 0 && hoursAgo < 24) {
        buckets[23 - hoursAgo].count++;
      }
    }
    return buckets;
  }, [items]);

  // Most recent item per source
  const latestBySource = useMemo(() => {
    const map = {};
    for (const item of items) {
      if (!map[item.source] || new Date(item.published_at) > new Date(map[item.source].published_at)) {
        map[item.source] = item;
      }
    }
    return Object.entries(map).sort(
      (a, b) => new Date(b[1].published_at) - new Date(a[1].published_at)
    );
  }, [items]);

  const maxSourceCount = bySource[0]?.[1] || 1;
  const maxHourly      = Math.max(...hourlyBuckets.map(b => b.count), 1);
  const SVG_W          = 24 * 22;

  if (loading) return <p className="text-gray-400 text-sm">Loading…</p>;

  if (items.length === 0) return (
    <p className="text-gray-400 text-sm italic">No feed data yet. Run a monitor to populate.</p>
  );

  return (
    <div className="space-y-8">

      {/* Volume timeline */}
      <div>
        <h3 className="text-sm font-semibold text-gray-300 mb-3">
          Volume — last 24 hours
          <span className="text-gray-600 font-normal ml-2">({items.length} items · peak {maxHourly}/hr)</span>
        </h3>
        <div className="bg-gray-900 border border-gray-700 rounded p-4">
          <svg viewBox={`0 0 ${SVG_W} 80`} className="w-full" style={{ height: '80px' }} preserveAspectRatio="none">
            {hourlyBuckets.map((b, i) => {
              const barH = Math.max(Math.round((b.count / maxHourly) * 70), b.count > 0 ? 2 : 0);
              return (
                <rect
                  key={i}
                  x={i * 22 + 1}
                  y={80 - barH}
                  width={20}
                  height={barH}
                  fill="#22c55e"
                  opacity="0.75"
                />
              );
            })}
          </svg>
          <div className="flex justify-between text-xs text-gray-600 mt-1">
            <span>{hourlyBuckets[0]?.label}</span>
            <span>{hourlyBuckets[11]?.label}</span>
            <span>{hourlyBuckets[23]?.label}</span>
          </div>
        </div>
      </div>

      {/* Source breakdown */}
      <div>
        <h3 className="text-sm font-semibold text-gray-300 mb-3">Items by source</h3>
        <div className="space-y-2">
          {bySource.map(([src, count]) => (
            <div key={src} className="flex items-center gap-3">
              <span className="text-xs text-blue-400 w-36 shrink-0 truncate">
                {SOURCE_LABELS[src] || src}
              </span>
              <div className="flex-1 bg-gray-800 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full"
                  style={{ width: `${Math.max((count / maxSourceCount) * 100, 1)}%` }}
                />
              </div>
              <span className="text-xs text-gray-400 w-8 text-right tabular-nums">{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Latest per source */}
      <div>
        <h3 className="text-sm font-semibold text-gray-300 mb-3">Latest per source</h3>
        <div className="space-y-2">
          {latestBySource.map(([src, item]) => (
            <div key={src} className="border border-gray-700 rounded p-3 bg-gray-900 flex items-start gap-3">
              <span className="text-xs text-blue-400 border border-blue-800 rounded px-1.5 py-0.5 shrink-0 mt-0.5">
                {SOURCE_LABELS[src] || src}
              </span>
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-sm text-green-400 hover:text-green-300 leading-snug min-w-0 truncate"
              >
                {item.title || item.url}
              </a>
              <span className="text-xs text-gray-500 shrink-0">{timeAgo(item.published_at)}</span>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}

// ─── Main Monitoring Page ────────────────────────────────────────────────────

export default function MonitoringPage() {
  const [tab, setTab] = useState('monitors');

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-green-400 mb-1">Monitoring</h2>
        <p className="text-sm text-gray-400">
          Track keywords, entities, and topics across live data feeds. Alerts surface when anomalies are detected.
        </p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-2 border-b border-gray-700 pb-2">
        {['monitors', 'feed', 'alerts', 'insights'].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 text-sm rounded-t capitalize font-mono transition-colors ${
              tab === t
                ? 'bg-gray-800 text-green-400 border border-gray-600 border-b-0'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'monitors' && <MonitorsTab />}
      {tab === 'feed'     && <FeedTab />}
      {tab === 'alerts'   && <AlertsTab />}
      {tab === 'insights' && <InsightsTab />}
    </div>
  );
}